import { useState, useEffect } from 'react';
import {
  Receipt, RefreshCw, AlertTriangle, ArrowLeft, ShoppingBag, Calculator,
  ExternalLink, Inbox, MapPin, User,
} from 'lucide-react';
import { plvApi, formatDate, getStatusInfo, getImageUrl, getTypeLabel } from '../api/plvApi';
import { formatPrice } from '../utils/pricingUtils';
import type { PLVOrder } from '../types/plv.types';

interface OrderDetailsProps {
  orderId: number;
  onBack: () => void;
}

export function OrderDetails({ orderId, onBack }: OrderDetailsProps) {
  const [order, setOrder] = useState<PLVOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadOrderDetails();
  }, [orderId]);

  const loadOrderDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await plvApi.getOrder(orderId);
      setOrder(data);
    } catch {
      setError('Erreur lors du chargement des détails de la commande');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12">
        <div className="tw-w-8 tw-h-8 tw-border-4 tw-border-affinicar-secondary tw-border-t-transparent tw-rounded-full tw-animate-spin" />
        <p className="tw-mt-3 tw-text-gray-500">Chargement des détails...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12">
        <AlertTriangle className="tw-w-12 tw-h-12 tw-text-red-400 tw-mb-3" />
        <h4 className="tw-text-lg tw-font-medium tw-text-red-600">Erreur de chargement</h4>
        <p className="tw-text-sm tw-text-gray-500 tw-mt-1 tw-mb-4">{error}</p>
        <div className="tw-flex tw-gap-2">
          <button
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-bg-affinicar-secondary tw-text-white hover:tw-opacity-90 tw-transition-opacity"
            onClick={loadOrderDetails}
          >
            <RefreshCw className="tw-w-4 tw-h-4" />
            Réessayer
          </button>
          <button
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-gray-300 tw-text-gray-600 hover:tw-bg-gray-50 tw-transition-colors"
            onClick={onBack}
          >
            <ArrowLeft className="tw-w-4 tw-h-4" />
            Retour aux commandes
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12 tw-text-gray-400">
        <Inbox className="tw-w-16 tw-h-16 tw-mb-3" />
        <h4 className="tw-text-lg tw-font-medium tw-text-gray-500">Commande introuvable</h4>
        <p className="tw-text-sm tw-mt-1 tw-mb-4">Impossible de trouver la commande #{orderId}.</p>
        <button
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-bg-affinicar-secondary tw-text-white hover:tw-opacity-90 tw-transition-opacity"
          onClick={onBack}
        >
          <ArrowLeft className="tw-w-4 tw-h-4" />
          Retour aux commandes
        </button>
      </div>
    );
  }

  const statusInfo = getStatusInfo(order.status);
  const totalItems = order.items?.length || 0;
  const totalHT =
    order.items?.reduce((sum, item) => sum + (Number(item.unitPriceHt) || 0), 0) || 0;

  const hasDeliveryContact =
    order.deliveryContactLastname || order.deliveryContactFirstname ||
    order.deliveryContactEmail || order.deliveryContactPhone;
  const hasDeliveryAddress =
    order.deliveryCompanyName || order.deliveryAddress ||
    order.deliveryZip || order.deliveryCity;

  return (
    <div className="tw-space-y-4">
      {/* Header */}
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
        <div className="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-items-start sm:tw-items-center tw-gap-3">
          <div>
            <h4 className="tw-text-lg tw-font-semibold tw-flex tw-items-center tw-gap-2">
              <Receipt className="tw-w-5 tw-h-5 tw-text-affinicar-secondary" />
              Commande {order.orderNumber}
            </h4>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mt-1">
              <span className={`tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-rounded-full ${statusInfo.colorClass}`}>
                {statusInfo.label}
              </span>
              <span className="tw-text-sm tw-text-gray-500">
                Créée le {formatDate(order.creationDate)}
              </span>
            </div>
          </div>
          <div className="tw-flex tw-gap-2">
            <button
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-gray-300 tw-text-gray-600 hover:tw-bg-gray-50 tw-transition-colors"
              onClick={loadOrderDetails}
            >
              <RefreshCw className="tw-w-4 tw-h-4" />
              Actualiser
            </button>
            <button
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-gray-300 tw-text-gray-600 hover:tw-bg-gray-50 tw-transition-colors"
              onClick={onBack}
            >
              <ArrowLeft className="tw-w-4 tw-h-4" />
              Commandes
            </button>
          </div>
        </div>
      </div>

      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4">
        {/* Information sidebar */}
        <div className="lg:tw-col-span-1 tw-space-y-4">
          {/* Delivery info */}
          {(hasDeliveryContact || hasDeliveryAddress) && (
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200">
              <div className="tw-px-4 tw-py-3 tw-border-b tw-border-gray-200">
                <h5 className="tw-text-sm tw-font-semibold tw-flex tw-items-center tw-gap-2">
                  <MapPin className="tw-w-4 tw-h-4 tw-text-gray-500" />
                  Livraison
                </h5>
              </div>
              <div className="tw-p-4 tw-space-y-3 tw-text-sm">
                {hasDeliveryContact && (
                  <div>
                    <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">
                      <User className="tw-w-3.5 tw-h-3.5" />
                      Contact
                    </div>
                    {(order.deliveryContactFirstname || order.deliveryContactLastname) && (
                      <div className="tw-font-medium">
                        {order.deliveryContactFirstname} {order.deliveryContactLastname}
                      </div>
                    )}
                    {order.deliveryContactEmail && (
                      <div className="tw-text-gray-500">{order.deliveryContactEmail}</div>
                    )}
                    {order.deliveryContactPhone && (
                      <div className="tw-text-gray-500">{order.deliveryContactPhone}</div>
                    )}
                  </div>
                )}
                {hasDeliveryAddress && (
                  <div>
                    <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1">
                      <MapPin className="tw-w-3.5 tw-h-3.5" />
                      Adresse
                    </div>
                    {order.deliveryCompanyName && (
                      <div className="tw-font-medium">{order.deliveryCompanyName}</div>
                    )}
                    {order.deliveryAddress && (
                      <div className="tw-text-gray-500">{order.deliveryAddress}</div>
                    )}
                    {(order.deliveryZip || order.deliveryCity) && (
                      <div className="tw-text-gray-500">
                        {order.deliveryZip}{order.deliveryZip && order.deliveryCity && ' '}{order.deliveryCity}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tracking */}
          {order.trackingNumber && (
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200">
              <div className="tw-px-4 tw-py-3 tw-border-b tw-border-gray-200">
                <h5 className="tw-text-sm tw-font-semibold">Suivi de livraison</h5>
              </div>
              <div className="tw-p-4">
                {order.trackingUrl ? (
                  <a
                    href={order.trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tw-inline-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-border-affinicar-secondary tw-text-affinicar-secondary hover:tw-bg-affinicar-lighter tw-transition-colors"
                  >
                    <ExternalLink className="tw-w-4 tw-h-4" />
                    {order.trackingNumber}
                  </a>
                ) : (
                  <span className="tw-inline-flex tw-items-center tw-px-2.5 tw-py-1 tw-text-sm tw-bg-gray-100 tw-rounded-lg">
                    {order.trackingNumber}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Summary card (mobile/tablet: after items, desktop: sidebar) */}
          <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200">
            <div className="tw-px-4 tw-py-3 tw-border-b tw-border-gray-200">
              <h5 className="tw-text-sm tw-font-semibold tw-flex tw-items-center tw-gap-2">
                <Calculator className="tw-w-4 tw-h-4 tw-text-gray-500" />
                Récapitulatif
              </h5>
            </div>
            <div className="tw-p-4 tw-space-y-2 tw-text-sm">
              <div className="tw-flex tw-justify-between">
                <span className="tw-text-gray-600">Nombre d'articles :</span>
                <span className="tw-font-medium">{totalItems}</span>
              </div>
              <div className="tw-flex tw-justify-between">
                <span className="tw-text-gray-600">Sous-total HT :</span>
                <span className="tw-font-medium">{formatPrice(totalHT)}</span>
              </div>
              <div className="tw-border-t tw-border-gray-200 tw-pt-2 tw-mt-2 tw-flex tw-justify-between tw-items-center">
                <span className="tw-font-semibold">Total HT :</span>
                <span className="tw-font-bold tw-text-lg tw-text-affinicar-secondary">
                  {formatPrice(totalHT)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="lg:tw-col-span-2">
          <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200">
            <div className="tw-px-4 tw-py-3 tw-border-b tw-border-gray-200">
              <h5 className="tw-text-sm tw-font-semibold tw-flex tw-items-center tw-gap-2">
                <ShoppingBag className="tw-w-4 tw-h-4 tw-text-gray-500" />
                Articles commandés ({totalItems})
              </h5>
            </div>
            {order.items && order.items.length > 0 ? (
              <div className="tw-divide-y tw-divide-gray-100">
                {order.items.map((item, index) => {
                  const itemImage = item.productImages?.[0];
                  const price = Number(item.unitPriceHt) || 0;

                  return (
                    <div key={item.id || index} className="tw-p-4">
                      <div className="tw-flex tw-gap-4">
                        {/* Image */}
                        <div className="tw-w-20 tw-h-20 tw-flex-shrink-0 tw-bg-gray-50 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-overflow-hidden">
                          {itemImage ? (
                            <img
                              src={itemImage.startsWith('http') ? itemImage : getImageUrl(itemImage)}
                              alt={item.productName || `Produit #${item.productId}`}
                              className="tw-max-h-full tw-max-w-full tw-object-contain"
                              onError={e => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <span className="tw-text-2xl">📦</span>
                          )}
                        </div>

                        {/* Details */}
                        <div className="tw-flex-1 tw-min-w-0">
                          <h6 className="tw-text-sm tw-font-semibold">
                            {item.productName || `Produit #${item.productId}`}
                          </h6>
                          {item.productDescription && (
                            <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5 tw-line-clamp-2">
                              {item.productDescription}
                            </p>
                          )}
                          {item.productType && (
                            <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-rounded tw-bg-gray-100 tw-text-gray-600 tw-mt-1">
                              {getTypeLabel(item.productType)}
                            </span>
                          )}
                        </div>

                        {/* Quantity & Price */}
                        <div className="tw-text-right tw-flex-shrink-0">
                          <div className="tw-text-sm tw-font-bold">
                            {price === 0 ? (
                              <span className="tw-text-green-600">GRATUIT</span>
                            ) : (
                              <>
                                {formatPrice(price)}
                                <span className="tw-text-xs tw-text-gray-400 tw-font-normal tw-ml-1">HT</span>
                              </>
                            )}
                          </div>
                          <div className="tw-text-xs tw-text-gray-500 tw-mt-0.5">
                            Qté : {item.quantity}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="tw-p-8 tw-text-center tw-text-gray-400">
                <Inbox className="tw-w-10 tw-h-10 tw-mx-auto tw-mb-2" />
                <p className="tw-text-sm">Aucun article dans cette commande</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
